import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatRupees } from '../helper';
import type { IAmountLimits, TAmountErrorKey } from '../helper';
import styles from '../styles.module.css';

interface AmountInputProps {
  value?: number;
  onChange: (raw: string) => void;
  limits: IAmountLimits;
  errorKey: TAmountErrorKey;
  autoFocus?: boolean;
  placeholder?: string;
}

const AmountInput: React.FC<AmountInputProps> = ({
  value,
  onChange,
  limits,
  errorKey,
  autoFocus,
  placeholder,
}) => {
  const { t } = useTranslation();

  const errorText = errorKey
    ? t(errorKey, {
        min: formatRupees(limits.min),
        max: formatRupees(limits.max),
        balance: formatRupees(limits.balance),
      })
    : '';

  return (
    <div className={styles.amountBlock}>
      <label
        className={clsx(styles.amountLabel, 'fontSizeSm12 fontColor666')}
        htmlFor="withdraw-amount"
      >
        {t('withdrawAmountLabel')}
      </label>

      <div
        className={clsx(styles.amountField, errorKey && styles.amountFieldError)}
      >
        <span className={clsx(styles.rupee, 'fontWeightBold fontSizeSm20')}>
          ₹
        </span>
        <input
          id="withdraw-amount"
          className={clsx(styles.amountInput, 'fontWeightBold fontSizeSm20')}
          // `inputMode` gives mobile a numeric keypad without the spinner and
          // leading-zero quirks of type="number".
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={value ?? ''}
          placeholder={placeholder ?? '0'}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(errorKey)}
          aria-describedby={errorKey ? 'withdraw-amount-error' : undefined}
        />
      </div>

      {errorKey ? (
        <p
          id="withdraw-amount-error"
          className={clsx(styles.amountError, 'fontSizeSm12')}
          role="alert"
        >
          {errorText}
        </p>
      ) : (
        limits.min !== undefined && (
          <p className={clsx(styles.amountHint, 'fontSizeSm12 fontColor666')}>
            {t('minWithdrawHint', { min: formatRupees(limits.min) })}
          </p>
        )
      )}
    </div>
  );
};

export default AmountInput;
